import { Component, AfterViewInit, OnInit, Input, OnChanges, SimpleChanges, HostListener, ViewChild } from '@angular/core';

import { MatterSearchComponent } from '../../common/matter-search/matter-search.component';

import { OutgoingJob } from '../../../models/outgoing-job';

import { AuthService } from '../../../services/common/auth.service';
import { NotyService } from '../../../services/common/noty.service';
import { OutgoingJobService } from '../../../services/outgoing/outgoing-job.service';

@Component({
  selector: 'app-outgoing-jobs-index',
  templateUrl: './outgoing-jobs-index.component.html',
  styleUrls: ['./outgoing-jobs-index.component.css'],
})
export class OutgoingJobsIndexComponent implements AfterViewInit, OnInit {
  /**
   * Matter id from parent component, used in matter show page
   */
  @Input() matter_id: number;

  /**
   * Indicates if to show minimal HTML of pagination and a simple table only
   */
  @Input() minimalMode: boolean = false;

  /**
   * Indicates if current user can process all outgoing jobs
   * @type {boolean}
   */
  canProcessAllOutgoingJobs: boolean = false;

  /**
   * Indicates count of total outgoing jobs in search results
   * @type {number}
   */
  count: number = 0;

  /**
   * Id if current user
   * @type {number}
   */
  currentUserId: number;

  /**
   * Indicates if the lock file is being deleted
   * @type {boolean}
   */
  deletingLockFile: boolean = false;

  /**
   * Indicates if finish action is called for the search results
   * @type {boolean}
   */
  finishingOutgoingJobs: boolean = false;

  /**
   * Outgoing jobs results for current page
   * @type {Array<OutgoingJob>}
   */
  outgoingJobs: Array<OutgoingJob> = [];

  /**
   * Indicates if print action is called for the search results
   * @type {boolean}
   */
  printingOutgoingJobs: boolean = false;

  /**
   * Indicates if any of the outgoing job is processing any action
   * @type {boolean}
   */
  processingAction: boolean = false;

  /**
   * Indicates if resubmit action is called for the search results
   * @type {boolean}
   */
  resubmittingOutgoingJobs: boolean = false;

  /**
   * Indicates if search is beind processed
   * @type {boolean}
   */
  searching: boolean = true;

  /**
   * Search options for the sidebar
   * @type {any}
   */
  searchOptions: any = {
    intranet_users: [],
  };

  /**
   * Search params for search requests
   * @type {any}
   */
  searchParams: any = {
    hide_finished: true,
    page: 1,
    per_page: 30,
  };

  /**
   * Indicates if the sidebar is maximized
   * @type {boolean}
   */
  sidebarMaximized: boolean = false;

  /**
   * Indicates if the sidebar is open for small width screens
   * @type {boolean}
   */
  sidebarOpen: boolean = false;

  loading: boolean = false;

  @ViewChild('matterSearch', { static: true }) matterSearchComponent: MatterSearchComponent;

  /**
   * Constructor
   * @param {AuthService}        private authService        Service for current user info and permissions
   * @param {NotyService}        private notyService        Notification service for web UI
   * @param {OutgoingJobService} private outgoingJobService Service to access outgoing jobs controller
   */
  constructor(private authService: AuthService, private notyService: NotyService, private outgoingJobService: OutgoingJobService) {}

  // Component lifecycle events

  /**
   * Called on component initialization
   */
  ngOnInit() {
    if (this.matter_id) this.searchParams.matter_id = this.matter_id;
    this.outgoingJobService.getOutgoingJobsSearchOptions().then((response_json) => {
      this.searchOptions = response_json;
      if (this.matter_id) this.searchParams.matter_id = this.matter_id;
      this.searchParams.per_page = response_json.per_page;
      this.search();
    });
    this.authService.getCurrentUserGroups().then(() => {
      this.currentUserId = this.authService.current_user_id;
      this.canProcessAllOutgoingJobs = this.authService.userGroups.indexOf('admin') > -1;
    });
  }

  /**
   * Called when the variables in the component change
   * @param {SimpleChanges} changes Angular changes object with changed variables of the component
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.matter_id && changes.matter_id.currentValue)
      this.outgoingJobService.getOutgoingJobsSearchOptions().then((response_json) => {
        this.searchOptions = response_json;
        if (this.matter_id) this.searchParams.matter_id = this.matter_id;
        this.searchParams.per_page = response_json.per_page;
        this.search();
      });
  }

  /**
   * Called after HTML view is initialized
   */
  ngAfterViewInit() {
    setTimeout(() => {
      this.content_sidebar_scroll();
      this.search_height_maintainer();
    }, 500);
    setTimeout(() => {
      this.content_sidebar_scroll();
      this.search_height_maintainer();
    }, 5000);
  }

  // Component methods

  /**
   * Clear search fields
   */
  clearSearch() {
    this.searchParams = {
      hide_finished: true,
      page: 1,
      per_page: this.searchOptions.per_page || 30,
    };
    this.matterSearchComponent.clearSelectedMatter();
    this.search();
  }

  /**
   * Continue outgoing job
   */
  continueOutgoingJob(outgoingJob: OutgoingJob) {
    outgoingJob.continuing = true;
    this.processingAction = true;
    this.outgoingJobService
      .start(outgoingJob.id)
      .then((response_json) => {
        outgoingJob.continuing = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job started', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error continuing outgoing job', 'error');
      })
      .catch(() => {
        outgoingJob.continuing = false;
        this.processingAction = false;
        this.notyService.notify('Error continuing outgoing job', 'error');
      });
  }

  /**
   * Delete lock file
   */
  deleteLockFile() {
    this.deletingLockFile = true;
    this.processingAction = true;
    this.outgoingJobService
      .deleteLockFile()
      .then((response_json) => {
        this.deletingLockFile = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Lock file deleted', 'success');
          this.searchOptions.lock_file_exists = false;
        } else this.notyService.notify(response_json.text || 'Error deletig lock file', 'error');
      })
      .catch(() => {
        this.deletingLockFile = false;
        this.processingAction = false;
        this.notyService.notify('Error deletig lock file', 'error');
      });
  }

  /**
   * Finish outgoing job
   * @param {OutgoingJob} outgoingJob Outgoing job to complete
   */
  finishOutgoingJob(outgoingJob: OutgoingJob) {
    outgoingJob.finishing = true;
    this.processingAction = true;
    this.outgoingJobService
      .sendout(outgoingJob.id)
      .then((response_json) => {
        outgoingJob.finishing = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job completed successfully', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error completing outgoing job', 'error');
      })
      .catch(() => {
        outgoingJob.finishing = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing job', 'error');
      });
  }

  /**
   * Finish outgoing jobs
   */
  finishOutgoingJobs() {
    this.finishingOutgoingJobs = true;
    this.processingAction = true;
    let outgoingJobIds: Array<number> = [];
    for (let outgoingJob of this.outgoingJobs) outgoingJobIds.push(outgoingJob.id);
    this.outgoingJobService
      .finish({
        jobs: outgoingJobIds.join(','),
      })
      .then((response_json) => {
        this.finishingOutgoingJobs = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job(s) are completed successfully', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error completing outgoing job(s)', 'error');
      })
      .catch(() => {
        this.finishingOutgoingJobs = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing job(s)', 'error');
      });
  }

  /**
   * Print outgoing jobs
   */
  printOutgoingJobs() {
    this.printingOutgoingJobs = true;
    this.processingAction = true;
    let outgoingJobIds: Array<number> = [];
    for (let outgoingJob of this.outgoingJobs) outgoingJobIds.push(outgoingJob.id);
    this.outgoingJobService
      .print(null, {
        jobs: outgoingJobIds.join(','),
      })
      .then((response_json) => {
        this.printingOutgoingJobs = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job(s) print action completed', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error processing print for outgoing job(s)', 'error');
      })
      .catch(() => {
        this.printingOutgoingJobs = false;
        this.processingAction = false;
        this.notyService.notify('Error processing print for outgoing job(s)', 'error');
      });
  }

  /**
   * Release outgoing job
   * @param {OutgoingJob} outgoingJob Outgoing job to release
   */
  releaseOutgoingJob(outgoingJob: OutgoingJob) {
    outgoingJob.releasing = true;
    this.processingAction = true;
    this.outgoingJobService
      .release(outgoingJob.id)
      .then((response_json) => {
        outgoingJob.releasing = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job released', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error releasing outgoing job', 'error');
      })
      .catch(() => {
        outgoingJob.releasing = false;
        this.processingAction = false;
        this.notyService.notify('Error releasing outgoing job', 'error');
      });
  }

  /**
   * Resubmit outgoing jobs
   */
  resubmitOutgoingJobs() {
    this.resubmittingOutgoingJobs = true;
    this.processingAction = true;
    let outgoingJobIds: Array<number> = [];
    for (let outgoingJob of this.outgoingJobs) outgoingJobIds.push(outgoingJob.id);
    this.outgoingJobService
      .print(null, {
        commit: 'resubmit',
        jobs: outgoingJobIds.join(','),
      })
      .then((response_json) => {
        this.resubmittingOutgoingJobs = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job(s) print action completed', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error processing print for outgoing job(s)', 'error');
      })
      .catch(() => {
        this.resubmittingOutgoingJobs = false;
        this.processingAction = false;
        this.notyService.notify('Error processing print for outgoing job(s)', 'error');
      });
  }

  /**
   * Process search request
   * @param {any = null} pageEvent Page change event with page change details, present if a pagination button is clicked
   */
  search(pageEvent: any = null, no_reload: boolean = false) {
    if (pageEvent) this.searchParams.page = pageEvent.page;
    else if (!no_reload) this.searchParams.page = 1;
    this.searchParams.minimal = this.minimalMode;
    if (this.searchParams.date_from_struct) this.searchParams.date_from = `${this.searchParams.date_from_struct.month}/${this.searchParams.date_from_struct.day}/${this.searchParams.date_from_struct.year}`;
    else this.searchParams.date_from = undefined;
    if (this.searchParams.date_to_struct) this.searchParams.date_to = `${this.searchParams.date_to_struct.month}/${this.searchParams.date_to_struct.day}/${this.searchParams.date_to_struct.year}`;
    else this.searchParams.date_to = undefined;
    this.searching = true;
    this.outgoingJobService
      .index(this.searchParams)
      .then((response_json) => {
        this.count = response_json.count;
        this.outgoingJobs = response_json.outgoing_jobs;
        this.searching = false;
      })
      .catch(() => {
        this.searching = false;
        this.notyService.notify('Error processing search', 'error');
      });
  }

  /**
   * Take outgoing job by current user
   * @param {OutgoingJob} outgoingJob Outgoing to take
   */
  takeOutgoingJob(outgoingJob: OutgoingJob) {
    outgoingJob.taking = true;
    this.processingAction = true;
    this.outgoingJobService
      .take(outgoingJob.id)
      .then((response_json) => {
        outgoingJob.taking = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job assignment successful', 'success');
          this.search(null, true);
        } else this.notyService.notify(response_json.text || 'Error taking outgoing job', 'error');
      })
      .catch(() => {
        outgoingJob.taking = false;
        this.processingAction = false;
        this.notyService.notify('Error taking outgoing job', 'error');
      });
  }

  // Positioning events and methods
  @HostListener('window:scroll', ['$event'])
  window_scroll(event: Event) {
    this.content_sidebar_scroll();
    this.search_height_maintainer();
  }

  @HostListener('window:resize', ['$event'])
  window_resize(event: Event) {
    this.content_sidebar_scroll();
    this.search_height_maintainer();
  }

  content_sidebar_scroll() {
    var content_main = document.getElementById('content-main');
    var content_sidebar = document.getElementById('content-sidebar');
    var sidebar_height = window.innerHeight;
    if (content_sidebar && content_main) {
      if (content_main.getBoundingClientRect().top > 19) {
        content_sidebar.style.position = 'absolute';
        content_sidebar.style.top = content_main.offsetTop + 'px';
        sidebar_height -= content_main.getBoundingClientRect().top + 20;
        //console.log('greater ' + matter_sidebar.style.top);
      } else {
        content_sidebar.style.position = 'fixed';
        content_sidebar.style.top = '20px';
        sidebar_height -= content_sidebar.getBoundingClientRect().top + 20;
        //console.log('lesser ' + matter_sidebar.style.top);
      }

      if (content_sidebar.clientHeight != sidebar_height) content_sidebar.style.height = sidebar_height + 'px';
    }
  }

  search_height_maintainer() {
    var content_sidebar = document.getElementById('content-sidebar');
    var content_search = document.getElementById('search-form');
    if (content_sidebar && content_search) {
      var search_height = content_sidebar.clientHeight - 95;
    }
  }
}
